import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { C } from "../../../lib/tokens.js";
import { apiFetch } from "../../../lib/api.js";
import { adaptRequest } from "../../../lib/useRequests.js";
import { requestUrlKey } from "../../../lib/utils.js";
import { Button, IconButton } from "../../../components/ui/Button.jsx";
import { Modal } from "../../../components/ui/Modal.jsx";
import { Spinner } from "../../../components/ui/Misc.jsx";
import { StatusChip, TypeChip } from "../../../components/ui/Chips.jsx";
import { InfiniteScrollList } from "../views/shared.jsx";
import { useViewport } from "../../../lib/useViewport.js";

function LinkRequestModal({ request, onUpdate, onClose }) {
  const { isMobile } = useViewport();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const abortRef = useRef(null);

  // Search the unscoped /requests/search endpoint (or, with no query, a plain
  // unscoped /requests list) rather than filtering the board's own (potentially
  // "assigned to me"-filtered) requests list — otherwise linking can only ever
  // reach whatever subset the Kanban board currently shows.
  //
  // /requests/search never paginates (by design, per openapi.yaml — it's a
  // type-ahead endpoint), so pagination only applies to the empty-query default
  // browse list, which hits plain GET /requests (same cursor pagination as the board).
  useEffect(() => {
    const trimmed = query.trim();
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setCursor(null);
    const path = trimmed
      ? `/requests/search?q=${encodeURIComponent(trimmed)}&limit=25`
      : `/requests?limit=25&sort=-updated_at&scope=all`;
    const timer = setTimeout(() => {
      apiFetch(path, { signal: ctrl.signal })
        .then((data) => {
          const list = Array.isArray(data) ? data : (data.data ?? []);
          setCandidates(list.map(adaptRequest).filter(Boolean).filter((r) => r.id !== request.id));
          setCursor(trimmed ? null : (data.next_cursor ?? null));
        })
        .catch((err) => {
          if (err.name !== "AbortError") setCandidates([]);
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setLoading(false);
        });
    }, trimmed ? 250 : 0);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [query, request.id]);

  async function loadMore() {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await apiFetch(`/requests?limit=25&sort=-updated_at&scope=all&cursor=${encodeURIComponent(cursor)}`);
      const list = Array.isArray(data) ? data : (data.data ?? []);
      const adapted = list.map(adaptRequest).filter(Boolean).filter((r) => r.id !== request.id);
      setCandidates((prev) => [...prev, ...adapted]);
      setCursor(data.next_cursor ?? null);
    } catch {
      // leave cursor as-is so the user can retry by scrolling again
    } finally {
      setLoadingMore(false);
    }
  }

  function handleLink() {
    if (!selected) return;
    onUpdate?.(request.id, { relatedId: selected.id });
    onClose();
  }

  return (
    <Modal onClose={onClose} width={520}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 15, fontWeight: 500, color: C.deepsea }}>Link a request</span>
        <button onClick={onClose} type="button" style={{ border: "none", background: "none", cursor: "pointer", padding: 4 }}>
          <span className="material-icons" style={{ fontSize: 18, color: C.iron }}>close</span>
        </button>
      </div>

      <div
        style={{
          display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
          border: `1px solid ${C.mercury}`, borderRadius: 4, background: C.platinum,
        }}
      >
        <span className="material-icons" style={{ fontSize: 16, color: C.iron }}>search</span>
        <input
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected(null);
          }}
          placeholder="Search by ID or summary…"
          style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 13, fontFamily: "Roboto", color: C.fg }}
        />
      </div>

      {loading ? (
        <div style={{ padding: "12px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 13, color: C.iron }}>
          <Spinner size={14} />
          {query.trim() ? "Searching…" : "Loading requests…"}
        </div>
      ) : candidates.length === 0 ? (
        <div style={{ padding: "12px 0", textAlign: "center", fontSize: 13, color: C.iron }}>
          No matching requests.
        </div>
      ) : (
        <InfiniteScrollList
          hasMore={!!cursor}
          onLoadMore={loadMore}
          loadingMore={loadingMore}
          style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 320, overflowY: "auto" }}
        >
          {candidates.map((r) => {
            const active = selected?.id === r.id;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setSelected(r)}
                style={{
                  display: "flex", flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "stretch" : "center", gap: isMobile ? 6 : 10,
                  padding: "8px 10px", borderRadius: 4,
                  border: `1px solid ${active ? C.liquid : C.mercury}`,
                  background: active ? C.rowActive : C.tin,
                  cursor: "pointer", textAlign: "left", fontFamily: "Roboto",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: isMobile ? "space-between" : "flex-start" }}>
                  <span style={{ fontFamily: "Roboto Mono", fontSize: 11, color: C.liquid, flex: "none" }}>#{r.id}</span>
                  <TypeChip id={r.type} size="sm" readOnly />
                  {!isMobile && (
                    <span style={{ fontSize: 13, color: C.fg, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.summary}
                    </span>
                  )}
                  <StatusChip id={r.status} size="sm" filled readOnly />
                </div>
                {isMobile && (
                  <span style={{ fontSize: 13, color: C.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.summary}
                  </span>
                )}
              </button>
            );
          })}
        </InfiniteScrollList>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: `1px solid ${C.mercury}`, paddingTop: 12 }}>
        <Button kind="outline" onClick={onClose}>Cancel</Button>
        <Button kind="primary" onClick={handleLink} disabled={!selected}>
          Link
        </Button>
      </div>
    </Modal>
  );
}

export function LinkedTab({ request, onUpdate }) {
  const [showModal, setShowModal] = useState(false);
  const [related, setRelated] = useState(null);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Following a link grows the breadcrumb trail by one entry (this request), so
  // the trail can be clicked back through however deep the chain goes, instead
  // of relying on browser history (which "Back" alone can't reconstruct).
  function openRelated(target = related) {
    const trail = [...(location.state?.requestTrail ?? []), { id: request.id, docNumber: request.docNumber, summary: request.summary }];
    navigate(`/requests/${requestUrlKey(target)}`, { state: { requestTrail: trail } });
  }

  // Requests that point at this one via R_RequestRelated_ID (the other direction of
  // relatedId above). Kept locally so an unlink shows at once.
  const [children, setChildren] = useState(request.related ?? []);
  const [unlinking, setUnlinking] = useState(null);
  useEffect(() => { setChildren(request.related ?? []); }, [request.related]);

  function unlinkChild(child) {
    setUnlinking(child.id);
    apiFetch(`/requests/${child.id}`, { method: "PATCH", body: JSON.stringify({ related_id: "" }) })
      .then(() => setChildren((cs) => cs.filter((c) => c.id !== child.id)))
      .catch(() => {})
      .finally(() => setUnlinking(null));
  }

  // Fetched directly by id rather than looked up in the board's (possibly
  // scope-filtered, e.g. "assigned to me") requests list — otherwise an existing
  // link to a request outside the current filter would wrongly show as unlinked.
  useEffect(() => {
    if (!request.relatedId) {
      setRelated(null);
      return;
    }
    let alive = true;
    setLoadingRelated(true);
    apiFetch(`/requests/${request.relatedId}`)
      .then((data) => { if (alive) setRelated(adaptRequest(data)); })
      .catch(() => { if (alive) setRelated(null); })
      .finally(() => { if (alive) setLoadingRelated(false); });
    return () => { alive = false; };
  }, [request.relatedId]);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Button kind="outline" icon="add_link" onClick={() => setShowModal(true)}>
          {related ? "Change link" : "Link request"}
        </Button>
      </div>
      {loadingRelated ? (
        <div
          style={{
            background: C.tin, border: `1px dashed ${C.mercury}`, borderRadius: 6,
            padding: 32, textAlign: "center", color: C.iron, fontSize: 13,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
          }}
        >
          <Spinner size={24} />
          Loading linked request…
        </div>
      ) : !related ? (
        <div
          style={{
            background: C.tin, border: `1px dashed ${C.mercury}`, borderRadius: 6,
            padding: 32, textAlign: "center", color: C.iron, fontSize: 13,
          }}
        >
          <span className="material-icons" style={{ fontSize: 32, color: C.silver, display: "block", marginBottom: 8 }}>link</span>
          {children.length > 0 ? "This request is not linked to another one." : "No linked request yet."}
        </div>
      ) : (
        <div
          onClick={() => openRelated()}
          style={{
            background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6, padding: 12,
            display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
          }}
        >
          <span style={{ fontFamily: "Roboto Mono", fontSize: 11, color: C.liquid }}>#{related.id}</span>
          <TypeChip id={related.type} size="sm" readOnly />
          <span style={{ fontSize: 13, color: C.fg, flex: 1, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {related.summary}
          </span>
          <StatusChip id={related.status} size="sm" filled readOnly />
          <IconButton icon="link_off" title="Remove link" onClick={(e) => { e.stopPropagation(); onUpdate?.(request.id, { relatedId: "" }); }} />
        </div>
      )}
      {children.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: C.iron, marginBottom: 8 }}>
            Related to this request ({children.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {children.map((c) => (
              <div key={c.id} onClick={() => openRelated(c)}
                style={{
                  background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6, padding: "10px 12px",
                  display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                }}>
                <span className="material-icons" style={{ fontSize: 15, color: C.silver }}>subdirectory_arrow_right</span>
                <span style={{ fontFamily: "Roboto Mono", fontSize: 11, color: C.liquid }}>#{c.docNumber ?? c.id}</span>
                <span style={{ fontSize: 13, color: C.fg, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.summary}
                </span>
                {unlinking === c.id ? <Spinner size={14} /> : (
                  <IconButton icon="link_off" title="Remove link" onClick={(e) => { e.stopPropagation(); unlinkChild(c); }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {showModal && (
        <LinkRequestModal request={request} onUpdate={onUpdate} onClose={() => setShowModal(false)} />
      )}
    </>
  );
}
